import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, Info, Loader2, ShieldAlert, ShieldCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";

import AppShell from "@/components/AppShell";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cspAdmin, me as meApi } from "@/lib/api";

/**
 * Turn a blocked_uri into the source token we'd add to the CSP. Returns
 * null for URIs that can't be allowlisted (unsafe-inline, unsafe-eval,
 * or a URI shape we don't understand yet).
 *
 * Examples:
 *   "https://cdn.example.com/foo/bar.png"  → "https://cdn.example.com"
 *   "data:image/svg+xml,..."               → "data:"
 *   "inline" / "eval"                       → null (dangerous keyword)
 *   ""  / null                              → null
 */
export function extractAllowlistableSource(blocked_uri) {
  if (!blocked_uri || typeof blocked_uri !== "string") return null;
  const trimmed = blocked_uri.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower === "inline" || lower === "eval" || lower === "unsafe-inline" || lower === "unsafe-eval") {
    return null;
  }
  // URL parser is strict — an unparseable value is not allowlistable.
  try {
    const url = new URL(trimmed);
    const proto = url.protocol; // includes trailing colon
    if (proto === "http:" || proto === "https:") {
      return `${proto}//${url.host}`;
    }
    if (["data:", "blob:", "mediastream:", "filesystem:"].includes(proto)) {
      return proto;
    }
  } catch {
    // Not a URL — could be an already-parsed keyword we don't handle.
  }
  return null;
}

/**
 * AdminCsp — owner-only Content-Security-Policy violation dashboard.
 *
 * Surfaces four views over the `csp_violations` collection (the report
 * collector at `POST /api/csp-report` writes to this table). Together
 * they answer the operator's actual question: *"Can I tighten the
 * enforced policy without breaking a real user?"*
 *
 *   • KPIs      — total in-window + enforce/report breakdown.
 *   • Directives— which CSP directive is being violated most.
 *   • Pairs     — the actionable table: directive × blocked_uri × count.
 *                 If a real CDN keeps showing up, that's the entry to
 *                 allowlist. If a random extension keeps showing up,
 *                 that's noise to ignore.
 *   • Recent    — timeline with directive filter + cursor pagination.
 *
 * Ownership check is server-side (`require_owner`), but we also probe
 * `me.isOwner()` up-front so we can 404 non-owners cleanly instead of
 * flashing a broken dashboard.
 */
export default function AdminCsp() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ownerState, setOwnerState] = useState("loading"); // "loading" | "yes" | "no"
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [directiveFilter, setDirectiveFilter] = useState("");
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Allowlist state — separately loaded so a slow summary doesn't block
  // the operator from seeing what's already accepted.
  const [allowlist, setAllowlist] = useState([]);
  const [allowlistLoading, setAllowlistLoading] = useState(false);
  const [allowlistBusyId, setAllowlistBusyId] = useState(null);
  // Confirmation dialog state — holds the pending (directive, source)
  // pair the operator is about to accept, plus any note they've typed.
  const [pendingAllow, setPendingAllow] = useState(null); // {directive, source} or null
  const [pendingNote, setPendingNote] = useState("");

  // Owner probe. Non-owners bounce back to /settings.
  useEffect(() => {
    let cancelled = false;
    meApi
      .isOwner()
      .then((r) => {
        if (cancelled) return;
        setOwnerState(r?.data?.is_owner ? "yes" : "no");
      })
      .catch(() => {
        if (!cancelled) setOwnerState("no");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Summary refresh.
  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const { data } = await cspAdmin.summary(days);
      setSummary(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("adminCsp.loadError"));
    } finally {
      setSummaryLoading(false);
    }
  }, [days, t]);

  const loadRecent = useCallback(
    async ({ append = false } = {}) => {
      setRecentLoading(true);
      try {
        const { data } = await cspAdmin.recent({
          limit: 50,
          before: append ? nextCursor : undefined,
          directive: directiveFilter || undefined,
        });
        setRecent((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
        setNextCursor(data.next_cursor || null);
        setHasMore(!!data.has_more);
      } catch (e) {
        toast.error(e?.response?.data?.detail || t("adminCsp.loadError"));
      } finally {
        setRecentLoading(false);
      }
    },
    [directiveFilter, nextCursor, t]
  );

  useEffect(() => {
    if (ownerState !== "yes") return;
    loadSummary();
  }, [ownerState, loadSummary]);

  useEffect(() => {
    if (ownerState !== "yes") return;
    // Whenever the directive filter changes, reset + reload.
    setRecent([]);
    setNextCursor(null);
    setHasMore(false);
    loadRecent();
  }, [ownerState, directiveFilter]);

  const loadAllowlist = useCallback(async () => {
    setAllowlistLoading(true);
    try {
      const { data } = await cspAdmin.listAllowlist();
      setAllowlist(data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("adminCsp.loadError"));
    } finally {
      setAllowlistLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (ownerState !== "yes") return;
    loadAllowlist();
  }, [ownerState, loadAllowlist]);

  const confirmAllowlist = useCallback(async () => {
    if (!pendingAllow) return;
    const { directive, source } = pendingAllow;
    setAllowlistBusyId(`${directive}|${source}`);
    try {
      await cspAdmin.addAllowlist({
        directive,
        source,
        note: pendingNote.trim() || null,
      });
      toast.success(t("adminCsp.allowlistAdded"));
      setPendingAllow(null);
      setPendingNote("");
      // Refresh both the allowlist section and the summary — accepted
      // rows will drop out of the top-blocked view once a new violation
      // window starts, but we refresh anyway so the operator sees the
      // system responding to their action.
      loadAllowlist();
      loadSummary();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? (detail?.code || t("adminCsp.allowlistFail")) : (detail || t("adminCsp.allowlistFail"));
      toast.error(msg);
    } finally {
      setAllowlistBusyId(null);
    }
  }, [pendingAllow, pendingNote, loadAllowlist, loadSummary, t]);

  const revokeAllowlist = useCallback(async (entry_id) => {
    setAllowlistBusyId(entry_id);
    try {
      await cspAdmin.removeAllowlist(entry_id);
      toast.success(t("adminCsp.allowlistRevoked"));
      loadAllowlist();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("adminCsp.allowlistFail"));
    } finally {
      setAllowlistBusyId(null);
    }
  }, [loadAllowlist, t]);

  // Non-owners: friendly "not available" state — no data leaked.
  if (ownerState === "no") {
    return (
      <AppShell>
        <div className="md:max-w-3xl md:mx-auto px-5 pt-6" data-testid="admin-csp-forbidden">
          <div className="bg-white rounded-3xl border border-[#E8E5DF] p-6 flex flex-col items-center text-center">
            <ShieldAlert className="w-6 h-6 text-rust" strokeWidth={1.5} />
            <p className="mt-3 text-leaf-ink">{t("adminCsp.forbidden")}</p>
            <Link
              to="/settings"
              className="mt-4 rounded-full bg-leaf text-white text-sm px-4 py-2 hover:bg-leaf-deep transition"
              data-testid="admin-csp-back-to-settings"
            >
              {t("adminCsp.backToSettings")}
            </Link>
          </div>
        </div>
      </AppShell>
    );
  }

  if (ownerState === "loading") {
    return (
      <AppShell>
        <div className="md:max-w-3xl md:mx-auto px-5 pt-10 flex items-center justify-center text-leaf-mist" data-testid="admin-csp-loading">
          <Loader2 className="w-4 h-4 animate-spin mr-2" strokeWidth={1.5} />
          {t("adminCsp.loading")}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="md:max-w-3xl md:mx-auto" data-testid="admin-csp-page">
        <div className="px-5 pt-1 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            aria-label={t("adminCsp.backAria")}
            data-testid="admin-csp-back"
            className="w-9 h-9 rounded-full bg-white border border-[#E8E5DF] flex items-center justify-center text-leaf-ink"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <h1 className="font-display text-3xl text-leaf-ink leading-tight">
            {t("adminCsp.title")}
          </h1>
        </div>

        <div className="px-5 mt-4 pb-8 space-y-5">
          <WindowPicker days={days} setDays={setDays} disabled={summaryLoading} />
          <KpiCards summary={summary} loading={summaryLoading} t={t} />
          <DirectiveList
            summary={summary}
            loading={summaryLoading}
            filter={directiveFilter}
            onFilter={setDirectiveFilter}
            t={t}
          />
          <PairsList
            summary={summary}
            loading={summaryLoading}
            allowlist={allowlist}
            onAllowlist={(directive, source) => {
              setPendingAllow({ directive, source });
              setPendingNote("");
            }}
            t={t}
          />
          <RecentTable
            items={recent}
            loading={recentLoading}
            hasMore={hasMore}
            onLoadMore={() => loadRecent({ append: true })}
            filter={directiveFilter}
            onClearFilter={() => setDirectiveFilter("")}
            t={t}
          />
          <AllowlistSection
            items={allowlist}
            loading={allowlistLoading}
            busyId={allowlistBusyId}
            onRevoke={revokeAllowlist}
            t={t}
          />
        </div>

        {/* Add-to-allowlist confirmation dialog. Kept at page level so it
            re-uses the same dialog primitive as the rest of the app and
            state coordination stays simple. */}
        <AlertDialog open={!!pendingAllow} onOpenChange={(v) => { if (!v) { setPendingAllow(null); setPendingNote(""); } }}>
          <AlertDialogContent data-testid="admin-csp-allowlist-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("adminCsp.allowlistConfirmTitle")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("adminCsp.allowlistConfirmBody")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {pendingAllow && (
              <div className="my-2 space-y-3">
                <div className="rounded-2xl bg-parchment border border-[#E8E5DF] p-3">
                  <code className="block text-xs text-leaf-mist">{pendingAllow.directive}</code>
                  <code className="block mt-1 text-sm text-leaf-ink break-all" data-testid="admin-csp-allowlist-preview-source">
                    {pendingAllow.source}
                  </code>
                </div>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.16em] text-leaf-mist">
                    {t("adminCsp.allowlistNoteLabel")}
                  </span>
                  <input
                    // Auto-focus so the operator lands on the field
                    // that carries the audit trail — the actual nudge.
                    // The dialog's own focus-trap will pull focus onto
                    // this element first as long as it renders BEFORE
                    // the confirm/cancel buttons, which it does.
                    autoFocus
                    type="text"
                    value={pendingNote}
                    onChange={(e) => setPendingNote(e.target.value)}
                    maxLength={200}
                    placeholder={t("adminCsp.allowlistNotePlaceholder")}
                    data-testid="admin-csp-allowlist-note-input"
                    className="mt-1 w-full rounded-full border border-[#E8E5DF] bg-white px-4 py-2 text-sm text-leaf-ink focus:outline-none focus:ring-2 focus:ring-leaf/50"
                  />
                  {/* Nudge: shown only when the note is empty. Deliberately
                      NOT a hard block — the operator might genuinely
                      want to accept without a note (throwaway allowlist
                      during triage, etc.) — but the Confirm button
                      text also changes so the empty-note path can't be
                      confused for the good-hygiene path. */}
                  {!pendingNote.trim() && (
                    <span
                      className="mt-1.5 flex items-start gap-1.5 text-[11px] text-rust"
                      data-testid="admin-csp-allowlist-note-nudge"
                    >
                      <Info className="w-3 h-3 mt-[1px] shrink-0" strokeWidth={1.75} />
                      {t("adminCsp.allowlistNoteNudge")}
                    </span>
                  )}
                </label>
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-csp-allowlist-cancel">
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmAllowlist}
                disabled={!!allowlistBusyId}
                data-testid="admin-csp-allowlist-confirm"
                className="bg-leaf hover:bg-leaf-deep text-white"
              >
                {allowlistBusyId ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" strokeWidth={1.5} /> : null}
                {/* Button copy calls out the missing note explicitly so
                    the empty-note path can't be misread as the tidy
                    one. Full stop: this is a soft nudge, not a block. */}
                {pendingNote.trim()
                  ? t("adminCsp.allowlistConfirmAction")
                  : t("adminCsp.allowlistConfirmActionNoNote")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Presentational subcomponents
// ────────────────────────────────────────────────────────────────────────

function WindowPicker({ days, setDays, disabled }) {
  const { t } = useTranslation();
  const options = [1, 7, 14, 30];
  return (
    <div className="bg-white rounded-3xl border border-[#E8E5DF] p-4" data-testid="admin-csp-window">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-[0.18em] text-leaf">
          {t("adminCsp.windowLabel")}
        </span>
        <div className="flex gap-2 flex-wrap">
          {options.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              disabled={disabled}
              data-testid={`admin-csp-window-${d}`}
              className={`rounded-full px-3 py-1 text-sm border transition ${
                days === d
                  ? "bg-leaf text-white border-leaf"
                  : "bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment"
              } disabled:opacity-60`}
            >
              {t("adminCsp.windowDays", { count: d })}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function KpiCards({ summary, loading, t }) {
  const total = summary?.total ?? 0;
  const enforce = summary?.by_disposition?.enforce ?? 0;
  const report = summary?.by_disposition?.report ?? 0;
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="admin-csp-kpis">
      <Kpi label={t("adminCsp.kpiTotal")} value={total} loading={loading} testId="admin-csp-kpi-total" />
      <Kpi label={t("adminCsp.kpiEnforce")} value={enforce} loading={loading} testId="admin-csp-kpi-enforce" tone="rust" />
      <Kpi label={t("adminCsp.kpiReport")} value={report} loading={loading} testId="admin-csp-kpi-report" tone="leaf" />
    </div>
  );
}

function Kpi({ label, value, loading, testId, tone = "leaf" }) {
  const toneClass = tone === "rust" ? "text-rust" : "text-leaf-deep";
  return (
    <div className="bg-white rounded-3xl border border-[#E8E5DF] p-4 flex flex-col" data-testid={testId}>
      <span className="text-[11px] uppercase tracking-[0.16em] text-leaf-mist">{label}</span>
      <span className={`mt-1 text-3xl font-display ${toneClass}`}>
        {loading ? <span className="text-leaf-mist text-base">···</span> : value.toLocaleString()}
      </span>
    </div>
  );
}

function DirectiveList({ summary, loading, filter, onFilter, t }) {
  const rows = summary?.by_directive || [];
  const max = useMemo(() => rows.reduce((m, r) => Math.max(m, r.count), 0) || 1, [rows]);
  return (
    <div className="bg-white rounded-3xl border border-[#E8E5DF] p-5" data-testid="admin-csp-directives">
      <div className="flex items-center gap-2 text-leaf">
        <h2 className="text-xs uppercase tracking-[0.18em] font-medium">{t("adminCsp.directivesTitle")}</h2>
      </div>
      <p className="mt-1 text-sm text-leaf-mist">{t("adminCsp.directivesHelp")}</p>
      {loading && rows.length === 0 ? (
        <div className="mt-3 text-leaf-mist text-sm">{t("adminCsp.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="mt-3 text-leaf-mist text-sm">{t("adminCsp.emptyDirectives")}</div>
      ) : (
        <ul className="mt-3 space-y-2">
          {rows.map((row) => (
            <li key={row.directive}>
              <button
                type="button"
                onClick={() => onFilter(filter === row.directive ? "" : row.directive)}
                data-testid={`admin-csp-directive-row-${row.directive}`}
                className={`w-full text-left rounded-2xl p-3 border transition ${
                  filter === row.directive ? "border-leaf bg-parchment" : "border-transparent hover:bg-parchment"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <code className="text-sm text-leaf-ink truncate">{row.directive || "unknown"}</code>
                  <span className="text-sm text-leaf-mist tabular-nums">{row.count.toLocaleString()}</span>
                </div>
                <div className="mt-1.5 h-1.5 bg-[#F0EBDF] rounded-full overflow-hidden">
                  <div className="h-full bg-leaf" style={{ width: `${Math.min(100, (row.count / max) * 100)}%` }} />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PairsList({ summary, loading, allowlist, onAllowlist, t }) {
  const rows = summary?.by_blocked_uri || [];
  // Build a set of already-accepted (directive, source) pairs so we
  // render "Accepted" instead of an active button.
  const acceptedSet = useMemo(() => {
    const s = new Set();
    for (const a of allowlist || []) {
      s.add(`${a.directive}|${a.source}`);
    }
    return s;
  }, [allowlist]);
  return (
    <div className="bg-white rounded-3xl border border-[#E8E5DF] p-5" data-testid="admin-csp-pairs">
      <div className="flex items-center gap-2 text-leaf">
        <h2 className="text-xs uppercase tracking-[0.18em] font-medium">{t("adminCsp.pairsTitle")}</h2>
      </div>
      <p className="mt-1 text-sm text-leaf-mist">{t("adminCsp.pairsHelp")}</p>
      {loading && rows.length === 0 ? (
        <div className="mt-3 text-leaf-mist text-sm">{t("adminCsp.loading")}</div>
      ) : rows.length === 0 ? (
        <div className="mt-3 text-leaf-mist text-sm">{t("adminCsp.emptyPairs")}</div>
      ) : (
        <ul className="mt-3 divide-y divide-[#F0EBDF]">
          {rows.map((row, idx) => {
            const source = extractAllowlistableSource(row.blocked_uri);
            const accepted = source && acceptedSet.has(`${row.directive}|${source}`);
            return (
              <li key={`${row.directive}-${row.blocked_uri}-${idx}`} className="py-3 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <code className="block text-xs text-leaf">{row.directive || "unknown"}</code>
                  <span className="block text-sm text-leaf-ink break-all">
                    {row.blocked_uri || <em className="text-leaf-mist">{t("adminCsp.emptyUri")}</em>}
                  </span>
                  <span className="block text-[11px] text-leaf-mist mt-0.5">
                    {t("adminCsp.lastSeen")} {formatDate(row.last_seen)}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-sm text-leaf-mist tabular-nums">{row.count.toLocaleString()}</span>
                  {accepted ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-leaf-deep bg-leaf/10 rounded-full px-2 py-0.5">
                      <Check className="w-3 h-3" strokeWidth={2} /> {t("adminCsp.allowlistAccepted")}
                    </span>
                  ) : source ? (
                    <button
                      type="button"
                      onClick={() => onAllowlist(row.directive, source)}
                      data-testid="admin-csp-allowlist-button"
                      className="inline-flex items-center gap-1 rounded-full bg-white border border-leaf/40 text-leaf-deep text-[11px] px-2.5 py-1 hover:bg-leaf/5 transition"
                    >
                      <ShieldCheck className="w-3 h-3" strokeWidth={1.5} /> {t("adminCsp.allowlistAction")}
                    </button>
                  ) : (
                    <span className="text-[11px] text-leaf-mist" title={t("adminCsp.allowlistNotAllowedHint")}>
                      {t("adminCsp.allowlistNotAllowed")}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AllowlistSection({ items, loading, busyId, onRevoke, t }) {
  return (
    <div className="bg-white rounded-3xl border border-[#E8E5DF] p-5" data-testid="admin-csp-allowlist-section">
      <div className="flex items-center gap-2 text-leaf">
        <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
        <h2 className="text-xs uppercase tracking-[0.18em] font-medium">{t("adminCsp.activeAllowlistTitle")}</h2>
      </div>
      <p className="mt-1 text-sm text-leaf-mist">{t("adminCsp.activeAllowlistHelp")}</p>
      {loading && items.length === 0 ? (
        <div className="mt-3 text-leaf-mist text-sm">{t("adminCsp.loading")}</div>
      ) : items.length === 0 ? (
        <div className="mt-3 text-leaf-mist text-sm">{t("adminCsp.emptyAllowlist")}</div>
      ) : (
        <ul className="mt-3 divide-y divide-[#F0EBDF]">
          {items.map((row) => (
            <li key={row.entry_id} className="py-3 flex items-start justify-between gap-3" data-testid="admin-csp-allowlist-row">
              <div className="min-w-0 flex-1">
                <code className="block text-xs text-leaf">{row.directive}</code>
                <code className="block text-sm text-leaf-ink break-all">{row.source}</code>
                {row.note && <span className="block text-[11px] text-leaf-mist mt-0.5">"{row.note}"</span>}
                <span className="block text-[11px] text-leaf-mist mt-0.5">
                  {t("adminCsp.addedBy")} {row.added_by_email || row.added_by || "unknown"} · {formatDate(row.added_at)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRevoke(row.entry_id)}
                disabled={busyId === row.entry_id}
                data-testid={`admin-csp-allowlist-revoke-${row.entry_id}`}
                className="inline-flex items-center gap-1 rounded-full bg-white border border-[#E8E5DF] text-rust text-[11px] px-2.5 py-1 hover:bg-rust/5 disabled:opacity-60 transition shrink-0"
              >
                {busyId === row.entry_id
                  ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.5} />
                  : <Trash2 className="w-3 h-3" strokeWidth={1.5} />}
                {t("adminCsp.allowlistRevoke")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecentTable({ items, loading, hasMore, onLoadMore, filter, onClearFilter, t }) {
  return (
    <div className="bg-white rounded-3xl border border-[#E8E5DF] p-5" data-testid="admin-csp-recent">
      <div className="flex items-center justify-between gap-2 text-leaf flex-wrap">
        <h2 className="text-xs uppercase tracking-[0.18em] font-medium">{t("adminCsp.recentTitle")}</h2>
        {filter && (
          <button
            type="button"
            onClick={onClearFilter}
            data-testid="admin-csp-clear-filter"
            className="rounded-full bg-parchment text-leaf-ink text-[11px] px-2.5 py-1 hover:bg-[#EEE9DE] transition"
          >
            {t("adminCsp.filteringBy")}: <code>{filter}</code> ✕
          </button>
        )}
      </div>
      {loading && items.length === 0 ? (
        <div className="mt-3 text-leaf-mist text-sm">{t("adminCsp.loading")}</div>
      ) : items.length === 0 ? (
        <div className="mt-3 text-leaf-mist text-sm">{t("adminCsp.emptyRecent")}</div>
      ) : (
        <ul className="mt-3 divide-y divide-[#F0EBDF]">
          {items.map((row, idx) => (
            <li key={`${row.ts}-${idx}`} className="py-3" data-testid="admin-csp-recent-row">
              <div className="flex items-center justify-between gap-2 text-[11px] text-leaf-mist">
                <span>{formatDate(row.ts)}</span>
                <span
                  className={`rounded-full px-2 py-0.5 ${
                    row.disposition === "enforce" ? "bg-rust/10 text-rust" : "bg-leaf/10 text-leaf-deep"
                  }`}
                >
                  {row.disposition || "unknown"}
                </span>
              </div>
              <code className="mt-1 block text-xs text-leaf">{row.directive || "unknown"}</code>
              <span className="mt-0.5 block text-sm text-leaf-ink break-all">
                {row.blocked_uri || <em className="text-leaf-mist">{t("adminCsp.emptyUri")}</em>}
              </span>
              {row.document_uri && (
                <span className="mt-0.5 block text-[11px] text-leaf-mist break-all">
                  {t("adminCsp.onPage")} {row.document_uri}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loading}
          data-testid="admin-csp-load-more"
          className="mt-4 w-full rounded-full bg-white border border-[#E8E5DF] text-leaf-ink text-sm py-2 hover:bg-parchment disabled:opacity-60 transition inline-flex items-center justify-center gap-1.5"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : null}
          {t("adminCsp.loadMore")}
        </button>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
